import React, { useEffect, useMemo, useRef } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { CountryType, SexType, StateType, SupportPatient, CreateClaimPatient } from '../../../../api';
import { getOptions } from '../../../base/select';
import { InputField, SelectField } from '../../../base/form';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import * as S from '../form.styles';
import { FormErrorBanner } from '../../../base/form/form-error-banner';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as Yup from 'yup';


interface PatientFormValues {
    patientLookupLastName?: string;
    patientId?: string;
    nickname?: string;
    first: string;
    middle?: string;
    last: string;
    dob?: string;
    sex?: SexType | '';
    externalId?: string; // MRN
    phone?: string;
    email?: string;
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

const emptyValues: PatientFormValues = {
    patientLookupLastName: '',
    patientId: '',
    nickname: '',
    first: '',
    middle: '',
    last: '',
    dob: '',
    sex: '' as any,
    externalId: '',
    phone: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: CountryType.US as any,
    postal: '',
};

interface IPatientFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const PatientForm: React.FunctionComponent<IPatientFormProps> = ({ claimEditorStore }) => {
    const sexOptions = getOptions(SexType);
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const formikRef = useRef<FormikProps<PatientFormValues> | null>(null);

    const PatientSchema = Yup.object().shape({
        first: Yup.string().required('First name is required'),
        middle: Yup.string(),
        last: Yup.string().required('Last name is required'),
        dob: Yup.string().required('Date of birth is required'),
        sex: Yup.mixed<SexType | ''>().oneOf(['' as any, ...Object.values(SexType) as any]).required('Sex is required'),
        externalId: Yup.string().required('MRN is required').max(38, 'MRN must be 38 characters or less'),
        phone: Yup.string(),
        email: Yup.string().email('Invalid email'),
        address1: Yup.string().required('Address Line 1 is required'),
        address2: Yup.string(),
        city: Yup.string().required('City is required'),
        state: Yup.mixed<StateType | ''>().required('State is required'),
        country: Yup.mixed<CountryType | ''>().required('Country is required'),
        postal: Yup.string().required('Postal Code is required'),
    });

    const getAddressLines = (address?: any): string[] => {
        if (Array.isArray(address?.lines) && address.lines.length > 0) return address.lines;
        return [address?.line1, address?.line2].filter(Boolean);
    };

    const getAddressPostal = (address?: any): string | undefined => {
        return address?.postal ?? address?.postalCode ?? address?.zip ?? address?.zipCode ?? undefined;
    };

    const initialValues = useMemo<PatientFormValues>(() => {
        const payload = (claimEditorStore?.activePayload as any) || {};
        const p: CreateClaimPatient | undefined = (claimEditorStore?.patient as any) || payload.patient;
        if (!p) {
            return emptyValues;
        }
        const primaryAddress = p.addresses?.[0];
        const addressLines = getAddressLines(primaryAddress);
        return {
            patientLookupLastName: p.last ?? '',
            patientId: ((p as any).patientId ?? (p as any).id) ?? undefined,
            nickname: (p as any).nickname ?? undefined,
            first: p.first ?? '',
            middle: p.middle ?? undefined,
            last: p.last ?? '',
            dob: (p.dob as any) ?? undefined,
            sex: ((p.sex as any) ?? '') as any,
            externalId: (p as any).externalId ?? undefined,
            phone: p.phones?.[0] ?? undefined,
            email: p.emails?.[0] ?? undefined,
            address1: addressLines[0] ?? '',
            address2: addressLines[1] ?? undefined,
            city: primaryAddress?.city ?? '',
            state: ((primaryAddress?.state as any) ?? '') as any,
            country: ((primaryAddress?.country as any) ?? CountryType.US) as any,
            postal: getAddressPostal(primaryAddress) ?? '',
        };
    }, [claimEditorStore?.activePayload]);

    const convertToPatient = (v: PatientFormValues, existingPatient?: CreateClaimPatient | null): CreateClaimPatient => {
        const line1 = v.address1?.trim();
        const line2 = v.address2?.trim();
        const lines = [line1, line2].filter(Boolean) as string[];
        const hasAddressValues = !!(
            lines.length ||
            v.city?.trim() ||
            v.state ||
            v.country ||
            v.postal?.trim()
        );

        const nextPatient: CreateClaimPatient = {
            patientId: v.patientId || undefined,
            nickname: v.nickname?.trim() ? v.nickname : undefined as any,
            first: v.first || '',
            middle: v.middle?.trim() ? v.middle : undefined as any,
            last: v.last || '',
            dob: (v.dob as any) ?? undefined,
            sex: (v.sex as any) || undefined,
            externalId: v.externalId?.trim() ? v.externalId : undefined as any,
            phones: v.phone?.trim() ? [v.phone] : [],
            emails: v.email?.trim() ? [v.email] : [],
        } as CreateClaimPatient;

        if (hasAddressValues) {
            nextPatient.addresses = [
                {
                    name: `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city?.trim() || undefined,
                    state: (v.state as any) || undefined,
                    postal: v.postal?.trim() || undefined,
                    country: (v.country as any) || undefined,
                } as any,
            ];
        } else if (
            existingPatient?.addresses &&
            existingPatient.patientId &&
            existingPatient.patientId === (v.patientId || undefined)
        ) {
            nextPatient.addresses = existingPatient.addresses;
        }

        return nextPatient;
    };

    const debouncedSave = useMemo(
        () =>
            _.debounce(() => {
                const latestValues = formikRef.current?.values as PatientFormValues | undefined;
                if (!latestValues) return;
                claimEditorStore.updatePatient(convertToPatient(latestValues, claimEditorStore.patient));
            }, 500),
        [claimEditorStore],
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
        };
    }, [debouncedSave]);

    return (
        <Formik<PatientFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validationSchema={PatientSchema}
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Save initial values to store on mount if store is null/empty
                const hasInitializedRef = useRef(false);
                useEffect(() => {
                    if (!hasInitializedRef.current) {
                        const storePatient = claimEditorStore?.patient;
                        // Only save if store is null/empty and we have initial values from payload
                        if (!storePatient && initialValues && (initialValues.first || initialValues.last)) {
                            const patientData = convertToPatient(initialValues, storePatient);
                            claimEditorStore?.updatePatient(patientData);
                        }
                        hasInitializedRef.current = true;
                    }
                }, []); // Only run once on mount

                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                // Respond to global validation trigger
                useEffect(() => {
                    if (claimEditorStore.validationSignal > 0) {
                        formik.submitForm();
                    }
                }, [claimEditorStore.validationSignal]);

                return (
                <S.FormContainer>
                    <FormErrorBanner submitCount={formik.submitCount} isValid={formik.isValid} errors={formik.errors} />
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Patient Lookup</S.Label>
                            <Field
                                name="patientLookup"
                                component={AsyncSelectField}
                                placeholder="Type last name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportPatient[] = await claimEditorStore.searchPatientsByLastName(q);
                                    return results.map(p => ({ 
                                        label: [p.first, p.middle, p.last].filter(Boolean).join(' '), 
                                        value: p.id as any, 
                                        patient: p,
                                        info: ' | ' + (p.dob || '') + ' | ' + (p.sex ? _.startCase(_.lowerCase(p.sex)) : ''),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const p: SupportPatient = opt?.patient;
                                    if (!p) {
                                        form.setFieldValue('patientId', undefined);
                                        return;
                                    }
                                    const primaryAddress = p.addresses?.[0];
                                    const addressLines = getAddressLines(primaryAddress);
                                    const newValues = {
                                        patientLookupLastName: p.last ?? '',
                                        patientId: p.id ?? undefined,
                                        nickname: (p as any).nickname ?? undefined,
                                        first: p.first ?? '',
                                        middle: p.middle ?? undefined,
                                        last: p.last ?? '',
                                        dob: (p.dob as any) ?? undefined,
                                        sex: (p.sex as any) ?? '',
                                        externalId: (p as any).externalId ?? undefined,
                                        phone: p.phones?.[0] ?? undefined,
                                        email: p.emails?.[0] ?? undefined,
                                        address1: addressLines[0] ?? '',
                                        address2: addressLines[1] ?? undefined,
                                        city: primaryAddress?.city ?? '',
                                        state: (primaryAddress?.state as any) ?? '',
                                        country: (primaryAddress?.country as any) ?? '',
                                        postal: getAddressPostal(primaryAddress) ?? '',
                                    };
                                    form.setValues(newValues);
                                    claimEditorStore.updatePatient(convertToPatient(newValues, claimEditorStore.patient));
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Preferred Name</S.Label>
                            <Field
                                name="nickname"
                                placeholder="Preferred Name"
                                component={InputField}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label required>Legal First Name</S.Label>
                            <Field
                                name="first"
                                placeholder="Legal First Name"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Legal Middle Name</S.Label>
                            <Field
                                name="middle"
                                placeholder="Legal Middle Name"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Legal Last Name</S.Label>
                            <Field
                                name="last"
                                placeholder="Legal Last Name"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Date of Birth</S.Label>
                            <Field
                                name="dob"
                                placeholder="YYYY-MM-DD"
                                component={DatePickerField}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label required>Sex</S.Label>
                            <Field
                                name="sex"
                                component={SelectField}
                                options={sexOptions as any}
                                placeholder="Select"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Medical Record Number (MRN)</S.Label>
                            <Field
                                name="externalId"
                                placeholder="MRN"
                                component={InputField}
                                maxLength={38 as any}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <Field
                                name="phone"
                                placeholder="Phone Number"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Email</S.Label>
                            <Field
                                name="email"
                                placeholder="Email"
                                component={InputField}
                                type="email"
                            />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field
                                name="address1"
                                placeholder="Address Line 1"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <Field
                                name="address2"
                                placeholder="Address Line 2"
                                component={InputField}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label required>City</S.Label>
                            <Field
                                name="city"
                                placeholder="City"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>State</S.Label>
                            <Field
                                name="state"
                                component={SelectField}
                                options={stateOptions as any}
                                placeholder="Select State"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Country</S.Label>
                            <Field
                                name="country"
                                component={SelectField}
                                options={countryOptions as any}
                                placeholder="Select Country"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Postal Code</S.Label>
                            <Field
                                name="postal"
                                placeholder="Postal Code"
                                component={InputField}
                            />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
};

export default PatientForm;


